import { updateLazy } from "Functions/lazy";
import { MarkerClusterer, SuperClusterAlgorithm } from "@googlemaps/markerclusterer";

import "Layouts/default";

// Helper function to calculate convex hull for polygon outline
function convexHull(points) {
    points.sort((a, b) => a[0] - b[0] || a[1] - b[1]);

    if (points.length <= 3) return points;

    const cross = (o, a, b) => {
        return (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
    };

    const lower = [];
    for (let i = 0; i < points.length; i++) {
        while (
            lower.length >= 2 &&
            cross(lower[lower.length - 2], lower[lower.length - 1], points[i]) <= 0
        ) {
            lower.pop();
        }
        lower.push(points[i]);
    }

    const upper = [];
    for (let i = points.length - 1; i >= 0; i--) {
        while (
            upper.length >= 2 &&
            cross(upper[upper.length - 2], upper[upper.length - 1], points[i]) <= 0
        ) {
            upper.pop();
        }
        upper.push(points[i]);
    }

    upper.pop();
    lower.pop();
    return lower.concat(upper);
}

// Helper function to group cities by route
function groupCitiesByRoute(cities) {
    const groups = {};

    cities.forEach((city) => {
        if (!city.routeId) return;

        if (!groups[city.routeId]) {
            groups[city.routeId] = [];
        }
        groups[city.routeId].push(city);
    });

    return groups;
}

// Helper function to get color for each route
function getRouteColor(routeId) {
    const colors = [
        "#3388ff",
        "#ff6b6b",
        "#4ecdc4",
        "#95e1d3",
        "#f38181",
        "#aa96da",
        "#fcbad3",
        "#a8d8ea",
        "#ffccbc",
        "#c5e1a5",
        "#80deea",
        "#ce93d8",
        "#90caf9",
        "#a5d6a7",
        "#ffab91",
    ];

    let hash = 0;
    for (let i = 0; i < routeId.length; i++) {
        hash = routeId.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
}

// Load Google Maps API dynamically
function loadGoogleMaps(apiKey) {
    return new Promise((resolve, reject) => {
        if (window.google && window.google.maps) {
            resolve();
            return;
        }

        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
        script.async = true;
        script.defer = true;

        script.onload = () => {
            if (window.google && window.google.maps) {
                resolve();
            } else {
                reject(new Error("Google Maps failed to load"));
            }
        };

        script.onerror = () => {
            reject(new Error("Failed to load Google Maps script"));
        };

        document.head.appendChild(script);
    });
}

// Initialize pickup map with Google Maps
function initializePickupMap(element, cities, apiKey) {
    // Get translatable texts from data attributes
    const $element = $(element);
    const textNoData = $element.data("text-no-data") || "Neboli nájdené žiadne dáta miest.";
    const textCities = $element.data("text-cities") || "obcí";
    const textDistrict = $element.data("text-district") || "Okres";
    const textPostcode = $element.data("text-postcode") || "PSČ";
    const textCitiesAtLocation = $element.data("text-cities-at-location") || "obcí na tomto mieste";

    if (!apiKey) {
        console.error("Google Maps API key is missing");
        element.innerHTML =
            '<div style="padding: 20px; text-align: center;">Chýba API kľúč pre Google Maps.</div>';
        return;
    }

    if (!cities || cities.length === 0) {
        console.error("No cities data available");
        element.innerHTML = `<div style="padding: 20px; text-align: center;">${textNoData}</div>`;
        return;
    }

    loadGoogleMaps(apiKey)
        .then(() => {
            // Create map centered on Slovakia with boundary restrictions
            const map = new google.maps.Map(element, {
                center: { lat: 48.7, lng: 19.5 },
                zoom: 8,
                minZoom: 7,
                maxZoom: 18,
                mapTypeControl: true,
                fullscreenControl: true,
                streetViewControl: false,
                restriction: {
                    latLngBounds: {
                        north: 49.65,
                        south: 47.7,
                        east: 22.65,
                        west: 16.8,
                    },
                    strictBounds: false,
                },
                // Custom map styling - clean, neutral look
                styles: [
                    {
                        featureType: "all",
                        elementType: "labels.text.fill",
                        stylers: [{ color: "#7c7c7c" }],
                    },
                    {
                        featureType: "all",
                        elementType: "labels.text.stroke",
                        stylers: [{ visibility: "off" }],
                    },
                    {
                        featureType: "water",
                        elementType: "geometry",
                        stylers: [{ color: "#d4e4ed" }],
                    },
                    {
                        featureType: "landscape",
                        elementType: "geometry",
                        stylers: [{ color: "#e8e6e1" }],
                    },
                    {
                        featureType: "road",
                        elementType: "geometry",
                        stylers: [{ color: "#ffffff" }],
                    },
                    {
                        featureType: "road",
                        elementType: "labels",
                        stylers: [{ visibility: "off" }],
                    },
                    {
                        featureType: "poi",
                        elementType: "all",
                        stylers: [{ visibility: "off" }],
                    },
                    {
                        featureType: "administrative",
                        elementType: "geometry.stroke",
                        stylers: [{ color: "#c9c9c9" }, { weight: 1 }],
                    },
                    {
                        featureType: "administrative.country",
                        elementType: "geometry.stroke",
                        stylers: [{ color: "#a0a0a0" }, { weight: 1.5 }],
                    },
                ],
            });

            // Group cities by coordinates (many cities share the same GPS location)
            const locationGroups = {};
            cities.forEach((city) => {
                const key = `${city.lat},${city.lng}`;
                if (!locationGroups[key]) {
                    locationGroups[key] = [];
                }
                locationGroups[key].push(city);
            });

            // Track currently open info window
            let currentInfoWindow = null;

            // Close info window when clicking on the map
            map.addListener("click", () => {
                if (currentInfoWindow) {
                    currentInfoWindow.close();
                    currentInfoWindow = null;
                }
            });

            // Create one marker per unique location
            const markers = [];
            Object.keys(locationGroups).forEach((key) => {
                const citiesAtLocation = locationGroups[key];
                const firstCity = citiesAtLocation[0];

                const color = "#008F46"; // Green lunys color

                const marker = new google.maps.Marker({
                    position: { lat: firstCity.lat, lng: firstCity.lng },
                    title:
                        citiesAtLocation.length > 1
                            ? `${citiesAtLocation.length} ${textCities}`
                            : firstCity.name,
                    icon: {
                        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
                                <circle cx="20" cy="20" r="17.5" fill="white"/>
                                <circle cx="20" cy="20" r="13.5" fill="${color}"/>
                                <g transform="translate(20, 20) scale(0.018) translate(-512, -512)">
                                    <path d="M512 240c-34.809 0-68.837 10.322-97.78 29.661s-51.502 46.826-64.823 78.987c-13.321 32.16-16.806 67.547-10.015 101.688s23.554 65.499 48.167 90.116c24.614 24.612 55.974 41.375 90.115 48.164 34.141 6.794 69.531 3.308 101.689-10.015 32.159-13.317 59.648-35.876 78.986-64.819 19.338-28.944 29.66-62.972 29.66-97.782-0.051-46.662-18.611-91.398-51.61-124.392-32.993-32.995-77.727-51.555-124.39-51.608zM512 496c-15.822 0-31.29-4.692-44.446-13.482s-23.41-21.285-29.465-35.903c-6.055-14.618-7.64-30.703-4.553-46.222s10.706-29.773 21.895-40.962c11.188-11.188 25.443-18.807 40.961-21.894s31.602-1.502 46.22 4.553c14.623 6.055 27.116 16.309 35.907 29.465s13.481 28.623 13.481 44.446c0 21.217-8.428 41.566-23.429 56.568-15.007 15.003-35.354 23.432-56.571 23.432zM512 48c-97.567 0.106-191.108 38.911-260.099 107.901s-107.795 162.531-107.901 260.099c0 309.437 326.56 541.599 340.48 551.322 8.067 5.642 17.674 8.673 27.52 8.673s19.451-3.031 27.52-8.673c61.85-45.583 118.441-97.905 168.719-156.001 112.358-129.039 171.761-265.841 171.761-395.32-0.108-97.567-38.912-191.108-107.904-260.099-68.987-68.99-162.529-107.796-260.096-107.901zM637.199 746.839c-38.139 43.812-80.031 84.204-125.199 120.719-45.169-36.516-87.062-76.908-125.2-120.719-66.8-77.358-146.8-197.601-146.8-330.839 0-72.139 28.657-141.323 79.667-192.333s120.194-79.667 192.333-79.667c72.141 0 141.322 28.657 192.333 79.667s79.667 120.195 79.667 192.333c0 133.238-80 253.481-146.801 330.839z" fill="white"/>
                                </g>
                            </svg>
                        `)}`,
                        scaledSize: new google.maps.Size(40, 40),
                        anchor: new google.maps.Point(20, 20),
                    },
                });

                // Store info in marker
                marker.routeId = firstCity.routeId;
                marker.citiesAtLocation = citiesAtLocation;

                // Create content showing all cities at this location
                let content = '<div style="padding: 8px; max-width: 300px;">';

                if (citiesAtLocation.length === 1) {
                    const city = citiesAtLocation[0];
                    content += `
                        <strong>${city.name}</strong><br>
                        ${textDistrict}: ${city.district}<br>
                        ${textPostcode}: ${city.postCode}
                    `;
                } else {
                    content += `<strong>${citiesAtLocation.length} ${textCitiesAtLocation}:</strong><br>`;
                    content +=
                        '<div style="max-height: 200px; overflow-y: auto; margin-top: 8px;">';
                    citiesAtLocation.forEach((city) => {
                        content += `
                            <div style="margin-bottom: 8px; padding-bottom: 8px; border-bottom: 1px solid #eee;">
                                <strong>${city.name}</strong><br>
                                <span style="font-size: 11px;">${textDistrict}: ${city.district} | ${textPostcode}: ${city.postCode}</span>
                            </div>
                        `;
                    });
                    content += "</div>";
                }

                content += "</div>";

                const infoWindow = new google.maps.InfoWindow({
                    content: content,
                });

                marker.addListener("click", function () {
                    // Close previously opened info window
                    if (currentInfoWindow) {
                        currentInfoWindow.close();
                    }
                    // Open this info window
                    infoWindow.open(map, marker);
                    currentInfoWindow = infoWindow;
                });

                markers.push(marker);
            });

            // Create marker clusterer with very aggressive clustering
            const clusterer = new MarkerClusterer({
                map,
                markers,
                algorithm: new SuperClusterAlgorithm({
                    radius: 150, // Larger radius = more aggressive clustering (default is 60)
                    maxZoom: 12, // Cluster up to zoom level 12 (default is 16)
                }),
                renderer: {
                    render: ({ count, position }, stats) => {
                        const color = "#008F46"; // Green lunys color

                        return new google.maps.Marker({
                            position,
                            icon: {
                                url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                                    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
                                        <circle cx="20" cy="20" r="17.5" fill="white"/>
                                        <circle cx="20" cy="20" r="13.5" fill="${color}"/>
                                        <text x="20" y="20" text-anchor="middle" dy=".35em" fill="white" font-family="Arial, sans-serif" font-size="11" font-weight="bold">
                                            ${count}
                                        </text>
                                    </svg>
                                `)}`,
                                scaledSize: new google.maps.Size(40, 40),
                                anchor: new google.maps.Point(20, 20),
                            },
                            zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
                        });
                    },
                },
            });

            // Store currently visible polygons
            let currentPolygons = [];

            // Add hover listeners to cluster markers
            google.maps.event.addListener(clusterer, "clusteringend", () => {
                const clusters = clusterer.clusters;

                clusters.forEach((cluster) => {
                    const clusterMarker = cluster.marker;

                    // On cluster hover, create and show polygon for THIS cluster only
                    clusterMarker.addListener("mouseover", () => {
                        // Clear previous polygons
                        currentPolygons.forEach((p) => p.setMap(null));
                        currentPolygons = [];

                        // Get coordinates of all markers in this specific cluster
                        const clusterCoords = [];
                        const coordSet = new Set();

                        cluster.markers.forEach((marker) => {
                            const pos = marker.getPosition();
                            const lat = pos.lat();
                            const lng = pos.lng();
                            const key = `${lat},${lng}`;

                            if (!coordSet.has(key)) {
                                coordSet.add(key);
                                clusterCoords.push([lat, lng]);
                            }
                        });

                        // Create polygon outline for this cluster if we have enough points
                        if (clusterCoords.length >= 3) {
                            const hull = convexHull(clusterCoords);
                            const polygonCoords = hull.map((point) => ({
                                lat: point[0],
                                lng: point[1],
                            }));

                            const polygon = new google.maps.Polygon({
                                paths: polygonCoords,
                                strokeColor: "#008F46",
                                strokeOpacity: 1,
                                strokeWeight: 2,
                                fillColor: "#008F46",
                                fillOpacity: 0.4,
                                map: map,
                            });

                            currentPolygons.push(polygon);
                        }
                    });

                    // On cluster mouse out, hide polygons
                    clusterMarker.addListener("mouseout", () => {
                        currentPolygons.forEach((p) => p.setMap(null));
                        currentPolygons = [];
                    });

                    // On cluster click, zoom to fit all markers in cluster
                    clusterMarker.addListener("click", () => {
                        // If cluster has only 1 marker, zoom to specific level
                        if (cluster.markers.length === 1) {
                            const marker = cluster.markers[0];
                            map.setCenter(marker.getPosition());
                            map.setZoom(15);
                        } else {
                            // Multiple markers - fit bounds to show all
                            const bounds = new google.maps.LatLngBounds();
                            cluster.markers.forEach((marker) => {
                                bounds.extend(marker.getPosition());
                            });
                            map.fitBounds(bounds);
                        }
                    });
                });
            });
        })
        .catch((error) => {
            console.error("Failed to load Google Maps:", error);
            element.innerHTML =
                '<div style="padding: 20px; text-align: center;">Nepodarilo sa načítať mapu. Skúste to znova.</div>';
        });
}

document.addEventListener("DOMContentLoaded", function () {
    updateLazy();

    // Initialize map
    const mapElement = document.querySelector(".js-pickup-map");
    if (mapElement && window.pickupMapData) {
        const { cities, googleMapsKey } = window.pickupMapData;
        initializePickupMap(mapElement, cities, googleMapsKey);
    } else {
        if (!mapElement) {
            console.error("Map element not found");
        }
        if (!window.pickupMapData) {
            console.error("Pickup map data not found");
        }
    }

    // Postal code autocomplete is handled by SmartForm (initialized via initAllSmartForms in layout)
    const $field = $(".js-pickup-post-code-validate");

    // Postal code verification button
    const $verifyButton = $(".js-verify-post-code");
    const $message = $(".js-post-code-message");

    if ($verifyButton.length && $field.length && $message.length) {
        // Get translatable texts from data attributes
        const textSuccess = $message.data("text-success") || "Na toto PSČ doručujeme";
        const textError = $message.data("text-error") || "Na toto PSČ nedoručujeme";
        const textEmpty = $message.data("text-empty") || "Zadajte PSČ.";
        const textAjaxError = $message.data("text-ajax-error") || "Chyba pri overovaní PSČ.";
        const textVerifying = $message.data("text-verifying") || "Overujem...";
        const textVerify = $message.data("text-verify") || "Overiť";

        $verifyButton.on("click", function () {
            const postCode = $field.val();

            if (!postCode || postCode.trim() === "") {
                $message.html(`<div class="c-msg c-msg--error">${textEmpty}</div>`);
                return;
            }

            $verifyButton.prop("disabled", true).text(textVerifying);
            $message.html("");

            $.ajax({
                url: "/validacia-psc",
                type: "post",
                dataType: "json",
                data: {
                    postCode: postCode,
                },
                success: function (response) {
                    if (response.status === true || response.status === "true") {
                        $message.html(`<div class="c-msg c-msg--success">${textSuccess}</div>`);
                    } else {
                        $message.html(`<div class="c-msg c-msg--error">${textError}</div>`);
                    }
                },
                error: function () {
                    $message.html(`<div class="c-msg c-msg--error">${textAjaxError}</div>`);
                },
                complete: function () {
                    $verifyButton.prop("disabled", false).text(textVerify);
                },
            });
        });

        // Allow pressing Enter to verify
        $field.on("keypress", function (e) {
            if (e.which === 13) {
                e.preventDefault();
                $verifyButton.click();
            }
        });
    }
});
